import * as Sentry from '@sentry/nuxt'
import { redactSensitiveParams, sanitizePII } from '~/utils/sentry'

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()
  const { isDemoMode } = useDemo()

  // Don't initialize Sentry in demo mode or if DSN is not configured
  if (isDemoMode.value || !config.public.sentryDsn) {
    console.log('🔍 Sentry: Disabled (demo mode or no DSN)')
    return
  }

  Sentry.init({
    dsn: config.public.sentryDsn,
    environment: config.public.sentryEnvironment || 'development',

    // Performance Monitoring
    // Sample 10% of transactions in production, 100% in development
    tracesSampleRate: config.public.sentryEnvironment === 'production' ? 0.1 : 1.0,

    // Set the release version (if available from build)
    release: config.public.gitCommit,

    // Configure integrations
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration({

        // Enable automatic instrumentation
        enableLongTask: true,
        enableInp: true
      })

      // Session replay for debugging (optional - can be resource intensive)
      // Uncomment to enable session replay
      // Sentry.replayIntegration({
      //   maskAllText: true,
      //   blockAllMedia: true,
      //   // Only record sessions with errors
      //   replaysSessionSampleRate: 0,
      //   replaysOnErrorSampleRate: 1.0,
      // }),
    ],

    // Privacy: Sanitize events before sending
    beforeSend(event) {
      // Redact sensitive URL parameters
      if (event.request?.url) {
        event.request.url = redactSensitiveParams(event.request.url)
      }

      // Sanitize breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
          if (breadcrumb.data) {
            breadcrumb.data = sanitizePII(breadcrumb.data as Record<string, unknown>)
          }
          return breadcrumb
        })
      }

      // Sanitize context data
      if (event.contexts) {
        event.contexts = sanitizePII(event.contexts as Record<string, unknown>) as typeof event.contexts
      }

      // Don't send events from demo mode (double-check)
      if (event.tags?.demo === 'true') {
        return null
      }

      return event
    },

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Network errors that aren't actionable
      'NetworkError',
      'Failed to fetch',
      // Common user errors
      'Non-Error promise rejection captured'
    ],

    // Deny URLs (third-party scripts)
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i
    ]
  })

  // Add demo mode tag to all events
  Sentry.setTag('demo', isDemoMode.value ? 'true' : 'false')

  console.log('🔍 Sentry: Initialized', {
    environment: config.public.sentryEnvironment,
    dsn: config.public.sentryDsn?.substring(0, 30) + '...'
  })
})
